/**
 * API client for incident categories and subcategories
 * PROJ-007: Incident Management - Category/Subcategory Lookup
 */

import { apiRequest } from './standard-api';

export interface Category {
  id: string;
  code: string;
  name: string;
  description?: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  subcategoriesCount?: number;
  incidentsCount?: number;
}

export interface Subcategory {
  id: string;
  categoryId: string;
  code: string;
  name: string;
  description?: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  category?: {
    id: string;
    code: string;
    name: string;
  };
  incidentsCount?: number;
}

interface CategoriesResponse {
  success: boolean;
  data: Category[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface SubcategoriesResponse {
  success: boolean;
  data: Subcategory[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Get all active categories
 */
export async function getActiveCategories(): Promise<Category[]> {
  const response = await apiRequest<Category[]>(
    '/incident/categories?isActive=true&limit=100',
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  // apiRequest returns ParsedApiResponse<T> with structure: { data, meta, raw }
  // The actual API response is in response.data
  if (!response.data) {
    throw new Error('Failed to load categories');
  }

  return response.data;
}

/**
 * Get all subcategories (optionally filtered by category)
 */
export async function getSubcategories(categoryId?: string): Promise<Subcategory[]> {
  const url = categoryId
    ? `/incident/subcategories?categoryId=${categoryId}&isActive=true&limit=100`
    : '/incident/subcategories?isActive=true&limit=100';

  const response = await apiRequest<Subcategory[]>(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  // apiRequest returns ParsedApiResponse<T> with structure: { data, meta, raw }
  if (!response.data) {
    throw new Error('Failed to load subcategories');
  }

  return response.data;
}

/**
 * Get category by ID with its subcategories
 */
export async function getCategoryById(id: string): Promise<Category & { subcategories: Subcategory[] }> {
  const response = await apiRequest<Category & { subcategories: Subcategory[] }>(
    `/incident/categories/${id}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  // apiRequest returns ParsedApiResponse<T> with structure: { data, meta, raw }
  if (!response.data) {
    throw new Error('Failed to load category');
  }

  return response.data;
}
